import type { PropsWithChildren, ReactNode, Ref } from 'react';
import styled from 'styled-components';
import type { AlignmentValue } from '@elfsight/configurator-contracts';
import { Typography } from '@elfsight/widget-ui';
import { useBusinessHoursProps } from '../business-hours-props-context';

const COMPACT_WIDTH = 100;

type ScheduleBlockProps = PropsWithChildren<{
  title?: ReactNode;
  largeOffsets?: boolean;
  itemsContainerRef?: Ref<HTMLDivElement>;
  compactMode?: boolean;
}>;

export function ScheduleBlock({
  children,
  title,
  itemsContainerRef,
  compactMode = false
}: ScheduleBlockProps) {
  const { textStyle, textColor, alignment } = useBusinessHoursProps();

  return (
    <Container>
      {!!title && (
        <Title
          textStyle={{
            ...textStyle,
            fontSize: textStyle.fontSize + 1,
            fontWeight: 'bold'
          }}
          color={textColor}
          _textAlign={alignment}
        >
          {title}
        </Title>
      )}
      <ItemsContainer
        ref={itemsContainerRef}
        _alignment={alignment}
        _compactMode={compactMode}
      >
        {children}
      </ItemsContainer>
    </Container>
  );
}

const Title = styled(Typography)<{ _textAlign?: string }>`
  ${({ _textAlign }) => !!_textAlign && `text-align: ${_textAlign}`};
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 320px;
`;

interface ItemsContainerProps {
  _alignment: AlignmentValue;
  _compactMode: boolean;
}
const ItemsContainer = styled.div<ItemsContainerProps>`
  display: grid;
  margin: 0;
  justify-items: ${({ _alignment }) => _alignment};

  grid-template-columns: ${({ _alignment, _compactMode }) => {
    const firstColumnWidth = (() => {
      if (_compactMode) {
        return `${COMPACT_WIDTH}px`;
      }

      if (_alignment === 'left') {
        return 'min-content';
      }

      return '1fr';
    })();
    const secondColumnWidth = _alignment === 'left' ? 'min-content' : '1fr';

    return `${firstColumnWidth} ${secondColumnWidth}`;
  }};

  gap: 0 40px;
`;
